import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Button from '../Button/Button';
import Header from '../Header/Header';
import Icon from '../Icon/Icon';
import Modal from '../Modal/Modal';
import Progressbar from '../Progressbar/Progressbar';
import SaveState from '../../constants/SaveState';

const STATES = [
  {},
  { icon: <Progressbar circular indeterminate width={22}/>, name: 'Saving' },
  { icon: <Icon name="ok" className="ok"/>, name: 'Saved' },
  { icon: <Icon name="error" className="error"/>, name: 'Error' },
];

/**
 * `Lightbox` is a modal component used for simple lightboxes. If your lightbox is more advanced, e.g. configuring an asset
 * or category you should use {@link TabbedLightbox} instead.
 *
 * The lightbox will automatically close when clicking outside the lightbox or hitting the Escape key unless
 * *closeOnClickOutside* is set to `false`.
 *
 * The lightbox will automatically be centered. Use CSS to set the desired width and height of the lightbox that best suits
 * your needs.
 *
 * vimond-core-components has a built-in "animate-lightbox" transition that can be used to with {@link TransitionGroup} to
 * animate lightboxes.
 *
 * @property {node} children The content of the lightbox.
 * @property {string} className The class name of the lightbox. Will be appended to "core-lightbox".
 * @property {boolean} closeOnClickOutside If set to `true`, the *onClose* callback will be called when the user clicks
 * outside the lightbox boundaries. Defaults to `true`.
 * @property {array} fields An array of objects of type `{ name, value }` properties that will be displayed in the sidebar.
 * The fields will be read-only.
 * @property {function} onAdd A callback which will be called when the **Add** button is clicked. The button will only be
 * created when this callback is present.
 * @property {function} onClose A callback that is called when the lightbox is closed.
 * @property {enum} saveState If set to either of the constants `SaveState.NONE`, `SaveState.SAVING`, `SaveState.SAVED` or
 * `SaveState.ERROR`, the corresponding state will be shown in the upper right corner of the lightbox. No state will be shown
 * by default.
 * @property {boolean} showClose If set to `true`, the lightbox will have a close button in the upper right corner. Defaults
 * to `true`.
 * @property {boolean} showHeader If set to `true`, the lightbox will have a a common header inside it. If set to `false` the
 * lightbox will fill a greater area of the screen by default. Defaults to `true`.
 * @property {string} title The title of the lightbox. This prop only makes sense when the *showHeader* prop is set to
 * `true`.
 * @property {node} toolbarChildren Custom components that will be added to the toolbar in the lightbox header. Will be
 * joined with the fields created by the *fields* prop.
 *
 * @see Modal, TabbedLightbox, TransitionGroup
 */
export default class Lightbox extends Component {

  static displayName = 'Lightbox';

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    closeOnClickOutside: PropTypes.bool, // eslint-disable-line
    fields: PropTypes.arrayOf(PropTypes.object),
    onAdd: PropTypes.func, // eslint-disable-line
    onClose: PropTypes.func, // eslint-disable-line
    saveState: PropTypes.oneOf([SaveState.SAVING, SaveState.SAVED, SaveState.ERROR, SaveState.NONE]),
    showClose: PropTypes.bool,
    showHeader: PropTypes.bool,
    title: PropTypes.string,
    toolbarChildren: PropTypes.node,
  };

  static defaultProps = {
    closeOnClickOutside: true,
    showClose: true,
    showHeader: true,
  };

  createFields(fields = []) {
    const items = [];

    fields.forEach((a, index) => {
      let value = a.value || '';

      if (typeof a.value === 'boolean') {
        value = <Icon name={a.value ? 'checkbox-checked' : 'checkbox-unchecked'} className="boolean" />;
      }
      items.push(
        <dt key={`${index}.1`}><label>{a.name}</label></dt>,
        <dd key={`${index}.2`}>{value}</dd>
      );
    });
    return items;
  }

  render() {
    const { className, children, fields, onAdd, onClose, showClose, showHeader, saveState, title, toolbarChildren, ...rest } = this.props;
    let attrs;
    let state;

    if (saveState) {
      state = (
        <span className="save-state">
          {STATES[saveState].icon}
          {STATES[saveState].name}
        </span>
      );
    }
    if (fields && fields.length) {
      attrs = (
        <dl className="fields">
          {this.createFields(fields)}
        </dl>
      );
    }
    return (
      <Modal {...rest} className={cx('core-lightbox', className, { simple: showHeader, custom: !showHeader })} onClose={onClose}>
        {showHeader && <Header title={title} onAdd={onAdd}>{attrs}{toolbarChildren}</Header>}
        {children}
        {showClose && <Button className="core-icon-button close" iconName="close" onClick={onClose}/>}
        {state}
      </Modal>
    );
  }

}
